import { useEffect, useRef, type MutableRefObject } from 'react';

/**
 * Asesor sin rostro: una esfera de luz en los colores del CEC que "respira" en
 * reposo, gira despacio mientras piensa, late con la voz del asesor al hablar y
 * con la voz de la persona mientras escucha (anillo dorado).
 *
 * El contorno se deforma con varias ondas de frecuencias no multiplos entre si:
 * la forma no se repite nunca, que es lo que evita la sensacion de bucle.
 */

export type OrbStatus = 'idle' | 'listening' | 'transcribing' | 'thinking' | 'speaking';

interface Props {
  status: OrbStatus;
  /** Volumen de la voz del asesor (0..1). */
  ampRef: MutableRefObject<number>;
  /** Volumen del microfono mientras escucha (0..1). */
  micLevelRef?: MutableRefObject<number>;
}

export function VoiceOrb({ status, ampRef, micLevelRef }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let frame = 0;
    let level = 0;
    let listen = 0;
    let think = 0;
    let spin = 0;
    let last = performance.now();
    // Fases aleatorias: cada visita, una esfera distinta.
    const phases = Array.from({ length: 5 }, () => Math.random() * Math.PI * 2);

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const { width, height } = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
    };
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    observer?.observe(canvas);
    resize();

    const draw = (now: number) => {
      frame = requestAnimationFrame(draw);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const t = now / 1000;
      const st = statusRef.current;
      const w = canvas.width;
      const h = canvas.height;
      const cx = w / 2;
      const cy = h / 2;
      const base = Math.min(w, h) * 0.26;

      const amp = st === 'speaking' ? Math.min(1, ampRef.current * 1.4) : 0;
      const mic = st === 'listening' ? Math.min(1, (micLevelRef?.current ?? 0) * 2.2) : 0;
      level += (Math.max(amp, mic) - level) * (1 - Math.exp(-dt / 0.08));
      listen += ((st === 'listening' ? 1 : 0) - listen) * (1 - Math.exp(-dt / 0.25));
      think += ((st === 'thinking' || st === 'transcribing' ? 1 : 0) - think) * (1 - Math.exp(-dt / 0.3));
      spin += dt * (0.25 + think * 1.6 + level * 0.8);

      ctx.clearRect(0, 0, w, h);

      // Fondo: degradado profundo con un halo que sigue la energia de la esfera.
      const bg = ctx.createRadialGradient(cx, cy, base * 0.2, cx, cy, Math.max(w, h) * 0.75);
      bg.addColorStop(0, `rgba(70, 86, 255, ${0.28 + level * 0.25})`);
      bg.addColorStop(0.45, 'rgba(22, 26, 70, 0.9)');
      bg.addColorStop(1, '#070a18');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      const breathe = reduced ? 0 : Math.sin(t * 1.45) * 0.025;
      const r0 = base * (1 + breathe + level * 0.2 + listen * 0.04);

      // Anillo dorado mientras escucha.
      if (listen > 0.01) {
        ctx.beginPath();
        ctx.arc(cx, cy, r0 * (1.28 + 0.05 * Math.sin(t * 3) + mic * 0.12), 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255, 204, 0, ${0.55 * listen})`;
        ctx.lineWidth = base * 0.035;
        ctx.stroke();
      }

      // Arcos que giran mientras piensa.
      if (think > 0.01) {
        for (let k = 0; k < 3; k += 1) {
          ctx.beginPath();
          const a = spin * (1 + k * 0.35) + k * 2.1;
          ctx.arc(cx, cy, r0 * (1.2 + k * 0.1), a, a + 0.9 + k * 0.2);
          ctx.strokeStyle = `rgba(185, 192, 255, ${0.5 * think * (1 - k * 0.25)})`;
          ctx.lineWidth = base * 0.02;
          ctx.lineCap = 'round';
          ctx.stroke();
        }
      }

      // La esfera: contorno deformado por ondas que no se repiten.
      const wobble = (reduced ? 0.01 : 0.035) + level * 0.085;
      const path = new Path2D();
      const N = 120;
      for (let i = 0; i <= N; i += 1) {
        const a = (i / N) * Math.PI * 2;
        const d =
          Math.sin(a * 3 + t * 0.9 + phases[0]!) * 0.5 +
          Math.sin(a * 5 - t * 1.37 + phases[1]!) * 0.3 +
          Math.sin(a * 2 + t * 0.53 + spin + phases[2]!) * 0.35 +
          Math.sin(a * 7 + t * 2.3 + phases[3]!) * 0.15 * (0.3 + level);
        const r = r0 * (1 + wobble * d);
        const x = cx + Math.cos(a) * r;
        const y = cy + Math.sin(a) * r;
        if (i === 0) path.moveTo(x, y);
        else path.lineTo(x, y);
      }
      path.closePath();

      ctx.save();
      ctx.shadowColor = `rgba(111, 123, 255, ${0.55 + level * 0.4})`;
      ctx.shadowBlur = base * (0.5 + level * 0.6);
      const fill = ctx.createRadialGradient(cx - r0 * 0.35, cy - r0 * 0.4, r0 * 0.1, cx, cy, r0 * 1.15);
      fill.addColorStop(0, '#c9d0ff');
      fill.addColorStop(0.35, '#6f7bff');
      fill.addColorStop(0.75, '#2c3cf0');
      fill.addColorStop(1, '#1a1f8f');
      ctx.fillStyle = fill;
      ctx.fill(path);
      ctx.restore();

      // Brillo interior que se desplaza despacio: da volumen y movimiento.
      const gx = cx + Math.cos(spin * 0.7 + phases[4]!) * r0 * 0.3;
      const gy = cy + Math.sin(spin * 0.5 + phases[4]!) * r0 * 0.3;
      const glow = ctx.createRadialGradient(gx, gy, 0, gx, gy, r0 * 0.8);
      glow.addColorStop(0, `rgba(255, 255, 255, ${0.28 + level * 0.3})`);
      glow.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.save();
      ctx.clip(path);
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    };
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [ampRef, micLevelRef]);

  return <canvas ref={canvasRef} className="orb" role="img" aria-label="Asesor virtual" />;
}
